import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from './components/ui/sonner';
import './App.css';

// Public pages
import HomePage from './pages/public/HomePage';
import AboutPage from './pages/public/AboutPage';
import DivisionsPage from './pages/public/DivisionsPage';
import AcademicsPage from './pages/public/AcademicsPage';
import AdmissionsPage from './pages/public/AdmissionsPage';
import StatusCheckerPage from './pages/public/StatusCheckerPage';
import CampusLifePage from './pages/public/CampusLifePage';
import NewsEventsPage from './pages/public/NewsEventsPage';
import ContactPage from './pages/public/ContactPage';

// Auth
import LoginPage from './pages/auth/LoginPage';

// Portals
import PortalLayout from './components/PortalLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import StudentManagement from './pages/admin/StudentManagement';
import TeacherManagement from './pages/admin/TeacherManagement';
import ParentManagement from './pages/admin/ParentManagement';
import ClassManagement from './pages/admin/ClassManagement';
import TimetableManagement from './pages/admin/TimetableManagement';
import AnnouncementManagement from './pages/admin/AnnouncementManagement';
import FinanceManagement from './pages/admin/FinanceManagement';
import AdmissionsManagement from './pages/admin/AdmissionsManagement';
import AcademicConfig from './pages/admin/AcademicConfig';
import AttendanceReports from './pages/admin/AttendanceReports';
import NewsEventsManagement from './pages/admin/NewsEventsManagement';
import EmailTemplates from './pages/admin/EmailTemplates';
import TeacherDashboard from './pages/teacher/TeacherDashboard';
import StudentDashboard from './pages/student/StudentDashboard';
import ParentDashboard from './pages/parent/ParentDashboard';

function ProtectedRoute({ children, allowedRoles }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
      <div className="text-center">
        <div className="w-12 h-12 rounded-full border-4 border-[#1B2A4A] border-t-[#C9A84C] animate-spin mx-auto mb-4" />
        <p className="text-slate-500 text-sm font-medium">Loading...</p>
      </div>
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;
  if (allowedRoles && !allowedRoles.includes(user.role)) return <Navigate to={`/${user.role}`} replace />;
  return children;
}

function PortalRoute({ children, allowedRoles }) {
  return (
    <ProtectedRoute allowedRoles={allowedRoles}>
      <PortalLayout>{children}</PortalLayout>
    </ProtectedRoute>
  );
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public */}
      <Route path="/" element={<HomePage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/divisions" element={<DivisionsPage />} />
      <Route path="/academics" element={<AcademicsPage />} />
      <Route path="/admissions" element={<AdmissionsPage />} />
      <Route path="/status-checker" element={<StatusCheckerPage />} />
      <Route path="/campus-life" element={<CampusLifePage />} />
      <Route path="/news-events" element={<NewsEventsPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/login" element={<LoginPage />} />

      {/* Admin Portal */}
      <Route path="/admin" element={<PortalRoute allowedRoles={['admin']}><AdminDashboard /></PortalRoute>} />
      <Route path="/admin/students" element={<PortalRoute allowedRoles={['admin']}><StudentManagement /></PortalRoute>} />
      <Route path="/admin/teachers" element={<PortalRoute allowedRoles={['admin']}><TeacherManagement /></PortalRoute>} />
      <Route path="/admin/parents" element={<PortalRoute allowedRoles={['admin']}><ParentManagement /></PortalRoute>} />
      <Route path="/admin/classes" element={<PortalRoute allowedRoles={['admin']}><ClassManagement /></PortalRoute>} />
      <Route path="/admin/timetable" element={<PortalRoute allowedRoles={['admin']}><TimetableManagement /></PortalRoute>} />
      <Route path="/admin/announcements" element={<PortalRoute allowedRoles={['admin']}><AnnouncementManagement /></PortalRoute>} />
      <Route path="/admin/finance" element={<PortalRoute allowedRoles={['admin']}><FinanceManagement /></PortalRoute>} />
      <Route path="/admin/admissions" element={<PortalRoute allowedRoles={['admin']}><AdmissionsManagement /></PortalRoute>} />
      <Route path="/admin/academic-config" element={<PortalRoute allowedRoles={['admin']}><AcademicConfig /></PortalRoute>} />
      <Route path="/admin/attendance-reports" element={<PortalRoute allowedRoles={['admin']}><AttendanceReports /></PortalRoute>} />
      <Route path="/admin/news-events" element={<PortalRoute allowedRoles={['admin']}><NewsEventsManagement /></PortalRoute>} />
      <Route path="/admin/email-templates" element={<PortalRoute allowedRoles={['admin']}><EmailTemplates /></PortalRoute>} />

      {/* Teacher Portal */}
      <Route path="/teacher" element={<PortalRoute allowedRoles={['teacher']}><TeacherDashboard /></PortalRoute>} />
      <Route path="/teacher/*" element={<PortalRoute allowedRoles={['teacher']}><TeacherDashboard /></PortalRoute>} />

      {/* Student Portal */}
      <Route path="/student" element={<PortalRoute allowedRoles={['student']}><StudentDashboard /></PortalRoute>} />
      <Route path="/student/*" element={<PortalRoute allowedRoles={['student']}><StudentDashboard /></PortalRoute>} />

      {/* Parent Portal */}
      <Route path="/parent" element={<PortalRoute allowedRoles={['parent']}><ParentDashboard /></PortalRoute>} />
      <Route path="/parent/*" element={<PortalRoute allowedRoles={['parent']}><ParentDashboard /></PortalRoute>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
